"use client";

import React, { useEffect, useRef, useState } from "react";
import { 
  Target,
  MousePointer,
  ArrowRight,
  ArrowLeft,
  X,
  Search,
  Filter
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";
import { useIsMobile } from "@/hooks/use-breakpoint";

// Base types for the interactive guide configuration
interface GuideElementStyle {
  width?: number | string;
  height?: number | string;
  backgroundColor?: string;
  borderColor?: string;
  borderRadius?: number | string;
  shadow?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'none';
  padding?: number | string;
}

interface GuideElementConfig {
  title: string;
  description: string;
  icon?: React.ComponentType<{ className?: string }>;
  targetSelector?: string; // CSS selector of the element to highlight
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center'; // Tooltip position
  spotlight?: boolean; // Enable spotlight highlight of the target element
  style?: GuideElementStyle; // Custom styles for the tooltip card
  showArrow?: boolean; // Show directional arrow
  arrowColor?: string; // Arrow color
  offset?: { x?: number; y?: number }; // Positional offset for fine-tuning
  // Visual tuning for spotlight
  spotlightPadding?: number; // extra padding around highlighted area
  spotlightRadius?: number; // corner radius for highlight ring
  overlayOpacity?: number; // darkness of the overlay
}


// Hook to get the absolute position and size of a target element
function useElementPosition(selector?: string, disableAutoScroll?: boolean, isMobile?: boolean) {
  const [position, setPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  useEffect(() => {
    if (!selector) return;

    const updatePosition = () => {
      const element = document.querySelector(selector);
      if (element) {
        const rect = element.getBoundingClientRect();
        setPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height,
        });
        // Ensure element is visible in viewport (only if auto-scroll is not disabled)
        if (!disableAutoScroll) {
          const fullyVisible =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);
          if (!fullyVisible || isMobile) {
            // On mobile, always scroll to center the spotlight
            (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          }
        }
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [selector, disableAutoScroll, isMobile]);

  return position;
}

// Spotlight overlay component
function Spotlight({ targetPosition, padding = 8, radius = 12, opacity = 0.6, targetSelector }: { targetPosition: { top: number; left: number; width: number; height: number } | null; padding?: number; radius?: number; opacity?: number; targetSelector?: string; }) {
  // If no targetPosition (mobile mode), show full overlay without hole
  if (!targetPosition) {
    return (
      <div className="fixed inset-0 pointer-events-none z-40">
        <div
          className="absolute inset-0 backdrop-blur-[1px]"
          style={{
            backgroundColor: `rgba(0,0,0,${opacity})`,
          }}
        />
      </div>
    );
  }

  // Find the main content area to limit spotlight ring area
  const mainContent = document.querySelector('main');
  const contentWrapper = document.querySelector('.content-wrapper');
  const header = document.querySelector('header');
  const footer = document.querySelector('footer');
  
  // Convert target absolute page coordinates to viewport coordinates for fixed overlay
  const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
  const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  const unclippedTop = targetPosition.top - padding - scrollY;
  const unclippedLeft = targetPosition.left - padding - scrollX;
  const unclippedBottom = unclippedTop + targetPosition.height + padding * 2;
  const unclippedRight = unclippedLeft + targetPosition.width + padding * 2;

  // Check if target element is inside header
  const targetElement = targetSelector ? document.querySelector(targetSelector) : null;
  const isTargetInHeader = targetElement && header && header.contains(targetElement);

  // Calculate safe content area bounds first
  let contentTop = 0;
  let contentLeft = 0;
  let contentBottom = viewportHeight;
  let contentRight = viewportWidth;

  // Only apply header clipping if target is NOT inside the header
  if (header && !isTargetInHeader) {
    const headerRect = header.getBoundingClientRect();
    contentTop = headerRect.bottom + window.scrollY;
  }

  if (footer) {
    const footerRect = footer.getBoundingClientRect();
    contentBottom = footerRect.top + window.scrollY;
  }

  if (contentWrapper) {
    const contentRect = contentWrapper.getBoundingClientRect();
    contentLeft = Math.max(contentLeft, contentRect.left + window.scrollX);
    contentRight = Math.min(contentRight, contentRect.right + window.scrollX);
  }

  // For overlay hole - clamp to content area bounds
  const overlayTop = Math.max(unclippedTop, contentTop - scrollY);
  const overlayLeft = Math.max(unclippedLeft, contentLeft - scrollX);
  const overlayBottom = Math.min(unclippedBottom, contentBottom - scrollY);
  const overlayRight = Math.min(unclippedRight, contentRight - scrollX);

  const overlayWidth = Math.max(0, overlayRight - overlayLeft);
  const overlayHeight = Math.max(0, overlayBottom - overlayTop);

  // For spotlight ring - use same bounds as overlay hole
  const ringTop = overlayTop;
  const ringLeft = overlayLeft;
  const ringWidth = overlayWidth;
  const ringHeight = overlayHeight;

  // Only show ring if it has valid dimensions and is within content bounds
  const shouldShowRing = ringWidth > 0 && ringHeight > 0;

  return (
    <>
      {/* Full page overlay with hole */}
      <div className="fixed inset-0 pointer-events-none z-40">
        {/* Dimmed overlay with blur, using CSS mask to cut rounded hole */}
        <div
          className="absolute inset-0 backdrop-blur-[1px]"
          style={{
            backgroundColor: `rgba(0,0,0,${opacity})`,
            WebkitMaskImage: `radial-gradient(transparent 0, transparent)`,
            maskComposite: 'exclude' as any,
            WebkitMaskComposite: 'xor' as any,
          }}
        />
        {/* Hole in overlay */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: overlayTop,
            left: overlayLeft,
            width: overlayWidth,
            height: overlayHeight,
            borderRadius: radius,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.6)',
          }}
        />
      </div>
      
      {/* Spotlight ring - only if within content bounds */}
      {shouldShowRing && (
        <div className="fixed inset-0 pointer-events-none z-40">
          <div
            className="absolute ring-2 ring-offset-2 ring-blue-500/80 ring-offset-transparent shadow-[0_0_40px_rgba(59,130,246,0.5)] animate-[pulse_1.6s_ease_in_out_infinite]"
            style={{
              top: ringTop,
              left: ringLeft,
              width: ringWidth,
              height: ringHeight,
              borderRadius: radius,
            }}
          />
        </div>
      )}
    </>
  );
}

// Directional arrow component
function Arrow({ 
  targetPosition, 
  tooltipPosition, 
  direction,
  color = 'text-blue-500'
}: { 
  targetPosition: { top: number; left: number; width: number; height: number } | null;
  tooltipPosition: { top: number; left: number };
  direction: 'top' | 'bottom' | 'left' | 'right';
  color?: string;
}) {
  if (!targetPosition) return null;

  const getArrowStyle = () => {
    const targetCenterX = targetPosition.left + targetPosition.width / 2;
    const targetCenterY = targetPosition.top + targetPosition.height / 2;
    
    switch (direction) {
      case 'top':
        return {
          top: targetPosition.top - 20,
          left: targetCenterX - 10,
          transform: 'rotate(180deg)',
        };
      case 'bottom':
        return {
          top: targetPosition.top + targetPosition.height + 10,
          left: targetCenterX - 10,
          transform: 'rotate(0deg)',
        };
      case 'left':
        return {
          top: targetCenterY - 10,
          left: targetPosition.left - 30,
          transform: 'rotate(90deg)',
        };
      case 'right':
        return {
          top: targetCenterY - 10,
          left: targetPosition.left + targetPosition.width + 10,
          transform: 'rotate(-90deg)',
        };
      default:
        return {};
    }
  };

  return (
    <div
      className={`fixed pointer-events-none z-50 ${color}`}
      style={getArrowStyle()}
    >
      <ArrowRight className="w-5 h-5 animate-bounce" />
    </div>
  );
}

// Base guide element component
interface GuideElementProps extends GuideElementConfig {
  onNext?: () => void;
  onPrev?: () => void;
  onSkip?: () => void;
  currentStep?: number;
  totalSteps?: number;
  showNavigation?: boolean;
  children?: React.ReactNode;
  // Force rendering of spotlight overlay even for left/right positions
  forceSpotlight?: boolean;
  // Disable automatic scrolling to target element
  disableAutoScroll?: boolean;
  // Optional custom position calculator provided by child components
  computePosition?: (args: {
    targetPosition: { top: number; left: number; width: number; height: number } | null;
    defaultWidth: number;
    defaultHeight: number;
    margin: number;
    getSpotlightBounds: () => { top: number; left: number; width: number; height: number };
  }) => { top?: number | string; left?: number | string; transform?: string; tooltipWidth?: number; tooltipHeight?: number } | null;
}

function GuideElement({
  title,
  description,
  icon,
  targetSelector,
  position = 'center',
  spotlight = false,
  style = {},
  showArrow = true,
  arrowColor = 'text-blue-500',
  offset = {},
  spotlightPadding = 8,
  spotlightRadius = 12,
  overlayOpacity = 0.6,
  onNext,
  onPrev,
  onSkip,
  currentStep,
  totalSteps,
  showNavigation = true,
  children,
  forceSpotlight = false,
  disableAutoScroll = false,
  computePosition,
}: GuideElementProps) {
  const isMobile = useIsMobile(); // Detect mobile device
  
  // On mobile, override position to 'center' for better UX
  const effectivePosition = isMobile && targetSelector ? 'center' : position;
  
  const targetPosition = useElementPosition(targetSelector, disableAutoScroll, isMobile);
  const IconComponent = icon;
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const [measuredScale, setMeasuredScale] = useState<number | null>(null);

  // Apply default styles and override with provided ones
  const defaultStyle: GuideElementStyle = {
    width: 384, // w-96
    backgroundColor: 'white',
    borderColor: 'transparent',
    borderRadius: '0.5rem',
    shadow: '2xl',
    padding: '1.5rem',
    ...style,
  };

  // Calculate tooltip position relative to the target element
  const getTooltipPosition = () => {
    const defaultWidth = typeof defaultStyle.width === 'number' ? defaultStyle.width : 384;
    const defaultHeight = 320;
    
    if (!targetPosition || !targetSelector || effectivePosition === 'center') {
      // With center mode we rely on flexbox container, so no explicit style
      return {
        top: undefined as unknown as string | number,
        left: undefined as unknown as string | number,
        transform: 'none',
        tooltipWidth: defaultWidth,
        tooltipHeight: defaultHeight,
      };
    }

    const margin = 20;

    // Helper: compute spotlight visible bounds in viewport coords
    const getSpotlightBounds = () => {
      const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
      const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
      const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 0;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

      // Raw, unclipped hole around target (viewport coords)
      const unclippedTop = targetPosition.top - (spotlightPadding || 0) - scrollY;
      const unclippedLeft = targetPosition.left - (spotlightPadding || 0) - scrollX;
      const unclippedBottom =
        unclippedTop + targetPosition.height + (spotlightPadding || 0) * 2;
      const unclippedRight =
        unclippedLeft + targetPosition.width + (spotlightPadding || 0) * 2;

      // Content bounds (viewport coords)
      let contentTop = 0;
      let contentLeft = 0;
      let contentBottom = viewportHeight;
      let contentRight = viewportWidth;

      const header = typeof document !== 'undefined' ? document.querySelector('header') : null;
      const footer = typeof document !== 'undefined' ? document.querySelector('footer') : null;
      const contentWrapper = typeof document !== 'undefined' ? document.querySelector('.content-wrapper') : null;

      if (header) {
        const r = header.getBoundingClientRect();
        contentTop = Math.max(contentTop, r.bottom);
      }
      if (footer) {
        const r = footer.getBoundingClientRect();
        contentBottom = Math.min(contentBottom, r.top);
      }
      if (contentWrapper) {
        const r = contentWrapper.getBoundingClientRect();
        contentLeft = Math.max(contentLeft, r.left);
        contentRight = Math.min(contentRight, r.right);
      }

      const left = Math.max(unclippedLeft, contentLeft);
      const top = Math.max(unclippedTop, contentTop);
      const right = Math.min(unclippedRight, contentRight);
      const bottom = Math.min(unclippedBottom, contentBottom);

      return {
        top,
        left,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
      };
    };

    // Allow child component to provide position, but still apply safety corrections
    if (typeof computePosition === 'function') {
      const custom = computePosition({
        targetPosition,
        defaultWidth,
        defaultHeight,
        margin,
        getSpotlightBounds,
      });
      if (custom) {
        let top = (custom.top as any) as number | string | undefined;
        let left = (custom.left as any) as number | string | undefined;
        let transform = custom.transform ?? 'none';
        let cWidth = custom.tooltipWidth ?? (typeof defaultStyle.width === 'number' ? defaultStyle.width : 384);
        let cHeight = custom.tooltipHeight ?? 320;

        const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
        const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;
        const spacing = margin;

        // If spotlight is used, ensure tooltip stays outside spotlight bounds
        if (spotlight) {
          const s = getSpotlightBounds();
          if (typeof left === 'number' && typeof top === 'number') {
            if (effectivePosition === 'left') {
              // keep to the left side
              cWidth = Math.min(cWidth, Math.max(220, s.left - spacing - 16));
              left = Math.min(left, s.left - spacing - cWidth);
            } else if (effectivePosition === 'right') {
              cWidth = Math.min(cWidth, Math.max(220, viewportWidth - 16 - (s.left + s.width + spacing)));
              left = Math.max(left, s.left + s.width + spacing);
              left = Math.min(left, viewportWidth - 16 - cWidth);
            } else if (effectivePosition === 'top') {
              cHeight = Math.min(cHeight, Math.max(200, s.top - spacing - 16));
              top = Math.min(top, s.top - spacing - cHeight);
            } else if (effectivePosition === 'bottom') {
              cHeight = Math.min(cHeight, Math.max(200, viewportHeight - 16 - (s.top + s.height + spacing)));
              top = Math.max(top, s.top + s.height + spacing);
              top = Math.min(top, viewportHeight - 16 - cHeight);
            }
          }
        }

        return {
          top: top as any,
          left: left as any,
          transform,
          tooltipWidth: cWidth,
          tooltipHeight: cHeight,
        };
      }
    }

    let tooltipWidth = typeof defaultStyle.width === 'number' ? defaultStyle.width : 384;
    let tooltipHeight = 320; // approximate height used for positioning

    let basePosition: { top: number | string; left: number | string; transform: string } = {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
    
    switch (effectivePosition) {
      case 'top':
        // Default: place above target, centered
        basePosition = {
          top: targetPosition.top - tooltipHeight - margin,
          left: targetPosition.left + targetPosition.width / 2 - tooltipWidth / 2,
          transform: 'none',
        };
        break;
      case 'bottom':
        basePosition = {
          top: targetPosition.top + targetPosition.height + margin,
          left: targetPosition.left + targetPosition.width / 2 - tooltipWidth / 2,
          transform: 'none',
        };
        break;
      case 'left':
        basePosition = {
          top: targetPosition.top + targetPosition.height / 2 - tooltipHeight / 2,
          left: targetPosition.left - tooltipWidth - margin,
          transform: 'none',
        };
        break;
      case 'right':
        basePosition = {
          top: targetPosition.top + targetPosition.height / 2 - tooltipHeight / 2,
          left: targetPosition.left + targetPosition.width + margin,
          transform: 'none',
        };
        break;
    }

    // Get viewport dimensions
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

    // If spotlight is on, ensure tooltip doesn't overlap the spotlight hole
    if (spotlight) {
      const s = getSpotlightBounds();
      const spacing = margin; // gap between tooltip and spotlight

      if (effectivePosition === 'left') {
        // Place strictly to the left of spotlight
        const availableRightEdge = Math.max(16, s.left - spacing);
        const maxWidth = Math.max(220, availableRightEdge - 16);
        tooltipWidth = Math.min(tooltipWidth, maxWidth);
        const desiredLeft = availableRightEdge - tooltipWidth;
        basePosition.left = Math.max(16, desiredLeft);
        // Vertically center to spotlight area
        basePosition.top = s.top + s.height / 2 - tooltipHeight / 2;
      } else if (effectivePosition === 'right') {
        const availableLeftEdge = Math.min(viewportWidth - 16, s.left + s.width + spacing);
        const maxWidth = Math.max(220, viewportWidth - 16 - availableLeftEdge);
        tooltipWidth = Math.min(tooltipWidth, maxWidth);
        basePosition.left = Math.min(viewportWidth - 16 - tooltipWidth, availableLeftEdge);
        basePosition.top = s.top + s.height / 2 - tooltipHeight / 2;
      } else if (effectivePosition === 'top') {
        const availableBottomEdge = Math.max(16, s.top - spacing);
        const maxHeight = Math.max(200, availableBottomEdge - 16);
        tooltipHeight = Math.min(tooltipHeight, maxHeight);
        basePosition.top = Math.max(16, availableBottomEdge - tooltipHeight);
        basePosition.left = s.left + s.width / 2 - tooltipWidth / 2;
      } else if (effectivePosition === 'bottom') {
        const availableTopEdge = Math.min(viewportHeight - 16, s.top + s.height + spacing);
        const maxHeight = Math.max(200, viewportHeight - 16 - availableTopEdge);
        tooltipHeight = Math.min(tooltipHeight, maxHeight);
        basePosition.top = Math.min(viewportHeight - 16 - tooltipHeight, availableTopEdge);
        basePosition.left = s.left + s.width / 2 - tooltipWidth / 2;
      }
    }

    // Make tooltip width adaptive to viewport on small screens
    // Clamp to 92vw with a sensible minimum
    const maxByViewport = Math.max(280, Math.floor(viewportWidth * 0.92));
    tooltipWidth = Math.min(tooltipWidth, maxByViewport);

    // Smart positioning: if tooltip would go off screen, adjust size and position
    if (typeof basePosition.left === 'number' && typeof basePosition.top === 'number') {
      const minLeft = 16;
      const maxLeft = viewportWidth - tooltipWidth - 16;
      const minTop = 16;
      const maxTop = viewportHeight - tooltipHeight - 16;

      // If tooltip goes off the left side, reduce width and adjust position
      if (basePosition.left < minLeft) {
        const availableSpace = targetPosition.left - minLeft - margin;
        if (availableSpace > 200) { // Minimum viable width
          tooltipWidth = Math.min(tooltipWidth, availableSpace);
          basePosition.left = targetPosition.left - tooltipWidth - margin;
        } else {
          // If not enough space on left, try positioning to the right
          basePosition.left = targetPosition.left + targetPosition.width + margin;
          if (basePosition.left + tooltipWidth > viewportWidth - 16) {
            // If still doesn't fit, position above
            basePosition.left = targetPosition.left + targetPosition.width / 2 - tooltipWidth / 2;
            basePosition.top = targetPosition.top - tooltipHeight - margin;
          }
        }
      }

      // Clamp final position within viewport
      basePosition.left = Math.max(minLeft, Math.min(maxLeft, basePosition.left));
      basePosition.top = Math.max(minTop, Math.min(maxTop, basePosition.top));
    }

    // Apply optional offsets
    if (offset.x && typeof basePosition.left === 'number') {
      basePosition.left = basePosition.left + offset.x;
    }
    if (offset.y && typeof basePosition.top === 'number') {
      basePosition.top = basePosition.top + offset.y;
    }

    return { ...basePosition, tooltipWidth, tooltipHeight };
  };

  const tooltipPosition = getTooltipPosition();
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 1080;

  // Compute adaptive scale to ensure tooltip fits viewport with safe margins
  const safeMargin = 16 * 2; // 16px on each side
  const rawWidth = (tooltipPosition.tooltipWidth || (typeof defaultStyle.width === 'number' ? defaultStyle.width : 384)) as number;
  const rawHeight = (tooltipPosition.tooltipHeight || 320) as number;
  const maxAllowedWidth = Math.max(280, viewportWidth - safeMargin);
  const maxAllowedHeight = Math.max(220, viewportHeight - safeMargin);
  let scaleX = Math.min(1, maxAllowedWidth / rawWidth);
  let scaleY = Math.min(1, maxAllowedHeight / rawHeight);
  let scale = Math.min(scaleX, scaleY);

  // After first render, measure real size and refine scale to remove internal scroll
  useEffect(() => {
    const el = tooltipRef.current;
    if (!el) return;

    const spacing = 20;
    let availableWidth = viewportWidth - 32;
    let availableHeight = viewportHeight - 32;

    if (spotlight && typeof tooltipPosition.left !== 'undefined') {
      // Compute available rectangle outside spotlight based on position
      const sGetter = () => {
        const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
        const scrollX = typeof window !== 'undefined' ? window.scrollX : 0;
        const rect = {
          top: 0,
          left: 0,
          width: viewportWidth,
          height: viewportHeight,
        };
        return rect;
      };
      // If we can access getSpotlightBounds from closure, prefer it
      try {
        // @ts-ignore - getSpotlightBounds exists inside getTooltipPosition; recompute minimal approx by querying targetPosition
        const s = (typeof targetPosition !== 'undefined' && targetPosition)
          ? {
              top: (targetPosition!.top - (spotlightPadding || 0)) - (typeof window !== 'undefined' ? window.scrollY : 0),
              left: (targetPosition!.left - (spotlightPadding || 0)) - (typeof window !== 'undefined' ? window.scrollX : 0),
              width: targetPosition!.width + (spotlightPadding || 0) * 2,
              height: targetPosition!.height + (spotlightPadding || 0) * 2,
            }
          : sGetter();

        if (effectivePosition === 'left') {
          availableWidth = Math.max(220, s.left - spacing - 16);
          availableHeight = viewportHeight - 32;
        } else if (effectivePosition === 'right') {
          availableWidth = Math.max(220, viewportWidth - 16 - (s.left + s.width + spacing));
          availableHeight = viewportHeight - 32;
        } else if (effectivePosition === 'top') {
          availableHeight = Math.max(200, s.top - spacing - 16);
          availableWidth = Math.min(viewportWidth - 32, Math.max(280, viewportWidth - 32));
        } else if (effectivePosition === 'bottom') {
          availableHeight = Math.max(200, viewportHeight - 16 - (s.top + s.height + spacing));
          availableWidth = Math.min(viewportWidth - 32, Math.max(280, viewportWidth - 32));
        }
      } catch {}
    }

    const rect = el.getBoundingClientRect();
    const sx = Math.min(1, availableWidth / rect.width);
    const sy = Math.min(1, availableHeight / rect.height);
    const newScale = Math.min(sx, sy);
    setMeasuredScale(prev => (prev === newScale ? prev : newScale));
  }, [viewportWidth, viewportHeight, spotlight, effectivePosition, tooltipPosition.left, tooltipPosition.top, targetPosition, spotlightPadding]);

  // Clamp numeric coordinates so the scaled tooltip stays fully in viewport
  const numericLeft = typeof tooltipPosition.left === 'number' ? tooltipPosition.left : undefined;
  const numericTop = typeof tooltipPosition.top === 'number' ? tooltipPosition.top : undefined;
  const effectiveWidth = rawWidth * scale;
  const effectiveHeight = rawHeight * scale;
  const minEdge = 16;
  const maxLeftClamp = viewportWidth - effectiveWidth - minEdge;
  const maxTopClamp = viewportHeight - effectiveHeight - minEdge;
  const clampedLeft = numericLeft !== undefined ? Math.max(minEdge, Math.min(maxLeftClamp, numericLeft)) : tooltipPosition.left;
  const clampedTop = numericTop !== undefined ? Math.max(minEdge, Math.min(maxTopClamp, numericTop)) : tooltipPosition.top;

  const tooltipStyle = {
    top: clampedTop as number | string,
    left: clampedLeft as number | string,
    // keep translate if provided, then scale so it stays centered/origin
    transform: `${tooltipPosition.transform || 'none'} scale(${measuredScale ?? scale})`,
    transformOrigin: 'top left' as const,
  };
  const tooltipWidth = rawWidth;
  const tooltipHeight = rawHeight;
  const isCenter = !targetPosition || !targetSelector || effectivePosition === 'center';

  // Map shadow enum to Tailwind classes
  const getShadowClass = (shadow: string) => {
    const shadowMap: Record<string, string> = {
      'sm': 'shadow-sm',
      'md': 'shadow-md',
      'lg': 'shadow-lg',
      'xl': 'shadow-xl',
      '2xl': 'shadow-2xl',
      'none': 'shadow-none',
    };
    return shadowMap[shadow] || 'shadow-2xl';
  };

  return (
    <>
      {/* Spotlight overlay */}
      {spotlight && (forceSpotlight || (effectivePosition !== 'left' && effectivePosition !== 'right') || isMobile) && (
        <Spotlight targetPosition={targetPosition} padding={spotlightPadding} radius={spotlightRadius} opacity={overlayOpacity} targetSelector={targetSelector} />
      )}
      
      {/* Directional arrow - hide on mobile */}
      {showArrow && targetSelector && targetPosition && effectivePosition !== 'center' && !isMobile && (
        <Arrow 
          targetPosition={targetPosition}
          tooltipPosition={{ 
            top: typeof tooltipStyle.top === 'number' ? tooltipStyle.top : 0, 
            left: typeof tooltipStyle.left === 'number' ? tooltipStyle.left : 0 
          }}
          direction={effectivePosition as 'top' | 'bottom' | 'left' | 'right'}
          color={arrowColor}
        />
      )}
      
      {/* Tooltip card */}
      <div className={`fixed z-50 ${isCenter ? 'flex items-center justify-center' : ''} pointer-events-none`} style={isCenter ? { inset: 0 } : {}}>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.3 }}
          className="pointer-events-auto relative min-w-0"
          style={isCenter ? undefined : tooltipStyle}
        >
        {showArrow && effectivePosition !== 'center' && !isMobile && (
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white" />
        )}
        <Card 
          className={`border-0 ${getShadowClass(defaultStyle.shadow || '2xl')}`}
          style={{
            width: isCenter ? defaultStyle.width : tooltipWidth,
            height: defaultStyle.height,
            backgroundColor: defaultStyle.backgroundColor,
            borderColor: defaultStyle.borderColor,
            borderRadius: defaultStyle.borderRadius,
            padding: defaultStyle.padding,
            overflow: 'hidden',
          }}
        >
          <CardHeader className="pb-4" ref={tooltipRef}>
            <div className="flex items-start justify-between gap-3 min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                {IconComponent && (
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <IconComponent className="h-5 w-5 text-white" />
                  </div>
                )}
                <CardTitle className="text-lg md:text-xl font-bold text-gray-900 break-words whitespace-normal">
                  {title}
                </CardTitle>
              </div>
              {showNavigation && currentStep !== undefined && totalSteps !== undefined && (
                <div className="text-xs text-gray-500 mt-1">{currentStep + 1} of {totalSteps}</div>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-4 break-words whitespace-normal" style={{ maxHeight: isCenter ? undefined : Math.floor(maxAllowedHeight / scale) - 88, overflowY: isCenter ? undefined : 'auto', overflowX: 'hidden' }}>
            <p className="text-gray-600 text-sm leading-relaxed">
              {description}
            </p>

            {children}
            
            {showNavigation && (onNext || onPrev || onSkip) && (
              <div className="flex items-center justify-between pt-2 gap-2 flex-wrap">
                <div className="flex gap-2 flex-wrap">
                  {onSkip && (
                    <Button variant="outline" size="sm" onClick={onSkip}>
                      Skip tour and sign in
                    </Button>
                  )}
                  {onPrev && currentStep !== undefined && currentStep > 0 && (
                    <Button variant="outline" size="sm" onClick={onPrev}>
                      <ArrowLeft className="mr-1 h-3 w-3" />
                      Back
                    </Button>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {onNext && (
                    <Button 
                      size="sm" 
                      onClick={onNext}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                    >
                      Next
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </motion.div>
      </div>
    </>
  );
}

// Exports
export { 
  GuideElement,
  type GuideElementProps,
  type GuideElementConfig,
  type GuideElementStyle
};
